import React, { useEffect, useMemo, useState } from "react";
import { Alert, DatePicker, Form, Modal, Select, TimePicker, message } from "antd";
import dayjs from "dayjs";
import { fetchSprNumberCategories, fetchSprNumberList } from "../../sprs/number/spr-number-api";
import { docExpBook, fetchBronFreeMest } from "./doc-exp-api";

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj?.[k] != null) return obj[k];
  }
  return undefined;
};

/**
 * Диалог брони существующей путёвки. Delphi: UEnterDateTimeBron + ARezerv.
 */
const DocExpBookModal = ({ open, idDoc, initial, onCancel, onDone }) => {
  const [form] = Form.useForm();
  const [numbers, setNumbers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [freeHint, setFreeHint] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([fetchSprNumberList(), fetchSprNumberCategories()])
      .then(([list, cats]) => {
        const items = Array.isArray(list) ? list : pick(list, "items", "Items") || [];
        setNumbers(
          items.map((n) => ({
            value: pick(n, "idNumber", "IdNumber"),
            label: pick(n, "name", "Name") || String(pick(n, "idNumber", "IdNumber")),
            idCat: pick(n, "idNumberCat", "IdNumberCat") || 0,
            childCount: pick(n, "childCount", "ChildCount") || 0
          })).filter((n) => n.value && !n.childCount)
        );
        const catItems = Array.isArray(cats) ? cats : pick(cats, "items", "Items") || [];
        setCategories(
          catItems.map((c) => ({
            value: pick(c, "idNumberCat", "IdNumberCat") || pick(c, "value", "Value"),
            label: pick(c, "name", "Name") || pick(c, "label", "Label")
          })).filter((c) => c.value)
        );
        form.setFieldsValue({
          idNumberCat: initial?.idNumberCat || undefined,
          idNumber: initial?.idNumber || undefined,
          period: [
            initial?.dateBeg ? dayjs(initial.dateBeg) : dayjs().startOf("day"),
            initial?.dateEnd ? dayjs(initial.dateEnd) : dayjs().add(7, "day").startOf("day")
          ],
          timeBeg: dayjs("14:00", "HH:mm"),
          timeEnd: dayjs("12:00", "HH:mm"),
          rebook: Boolean(initial?.dateBeg)
        });
      })
      .catch((e) => message.error(e.message || "Не удалось загрузить номера"))
      .finally(() => setLoading(false));
  }, [open, form, initial]);

  const idNumberCat = Form.useWatch("idNumberCat", form);
  const numberOptions = useMemo(() => {
    if (!idNumberCat) return numbers;
    return numbers.filter((n) => n.idCat === idNumberCat);
  }, [numbers, idNumberCat]);

  const refreshFree = async () => {
    const values = form.getFieldsValue();
    const idNumber = values.idNumber;
    const period = values.period;
    if (!idNumber || !period?.[0] || !period?.[1]) {
      setFreeHint("");
      return;
    }
    try {
      const res = await fetchBronFreeMest({
        idNumber,
        dateBeg: period[0].format("YYYY-MM-DD"),
        dateEnd: period[1].format("YYYY-MM-DD"),
        exceptDoc: idDoc
      });
      const free = pick(res, "freeMest", "FreeMest") ?? 0;
      setFreeHint(`Свободно основных мест: ${free}`);
    } catch {
      setFreeHint("");
    }
  };

  const onOk = async () => {
    try {
      const values = await form.validateFields();
      const dateBeg = values.period[0]
        .startOf("day")
        .hour(values.timeBeg.hour())
        .minute(values.timeBeg.minute())
        .second(0);
      const dateEnd = values.period[1]
        .startOf("day")
        .hour(values.timeEnd.hour())
        .minute(values.timeEnd.minute())
        .second(0);
      setSubmitting(true);
      const saved = await docExpBook(idDoc, {
        idNumber: values.idNumber,
        dateBeg: dateBeg.toISOString(),
        dateEnd: dateEnd.toISOString(),
        rebook: Boolean(values.rebook || initial?.dateBeg)
      });
      message.success("Размещение создано");
      onDone?.(saved);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || "Не удалось забронировать");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Бронь"
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={submitting || loading}
      okText="Разместить"
      destroyOnClose
      width={520}
    >
      <Form form={form} layout="vertical" onValuesChange={refreshFree}>
        <Form.Item name="idNumberCat" label="Категория">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            options={categories}
            placeholder="Все"
          />
        </Form.Item>
        <Form.Item
          name="idNumber"
          label="Номер"
          rules={[{ required: true, message: "Выберите номер" }]}
        >
          <Select showSearch optionFilterProp="label" options={numberOptions} />
        </Form.Item>
        <Form.Item
          name="period"
          label="Период"
          rules={[{ required: true, message: "Укажите период" }]}
        >
          <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="timeBeg" label="Время заезда" rules={[{ required: true }]}>
          <TimePicker format="HH:mm" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="timeEnd" label="Время выезда" rules={[{ required: true }]}>
          <TimePicker format="HH:mm" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="rebook" hidden>
          <input type="hidden" />
        </Form.Item>
      </Form>
      {freeHint ? <Alert type="info" showIcon message={freeHint} /> : null}
    </Modal>
  );
};

export default DocExpBookModal;
